/**
 * Site setup checklist — what every site needs based on project type, role,
 * and risky activities.
 *
 * The runtime in pages/SiteSetupChecklist.tsx walks this list, computes status
 * for each item by looking at existing docs / RAMS / COSHH / forms, and
 * renders them grouped by priority.
 */

import type { Site } from '../lib/db';
import type { OrgMembership } from '../lib/org';

export type SetupPriority = 'required' | 'recommended' | 'optional';

export type SetupStatus = 'done' | 'in_progress' | 'not_started' | 'not_required';

/**
 * What kind of thing is the user supposed to do? The runtime uses this to
 * (a) link to the right page and (b) check whether it's already been done.
 */
export type SetupTarget =
  | { kind: 'document'; templateId: string }              // org_documents OR site_documents row
  | { kind: 'rams' }                                       // any RAMS for this site
  | { kind: 'coshh' }                                      // any COSHH entry for this org
  | { kind: 'tbt' }                                        // any toolbox_talk form for this site
  | { kind: 'permit'; permitTemplateId?: string }          // any permit for this site (or specific)
  | { kind: 'subbie_induction' }                           // any subcontractor_induction form for this site
  | { kind: 'training' }                                   // any training records for this org
  | { kind: 'sign_in' }                                    // any sign-in for this site (or QR token)
  | { kind: 'external_link'; href: string };               // takes user to a page; status manual

export interface SetupItem {
  id: string;
  title: string;
  why: string;                // one-line reason this is needed
  priority: SetupPriority;
  target: SetupTarget;
}

// ---------- Helpers --------------------------------------------------------

interface Ctx {
  site: Site;
  org: OrgMembership;
  /** Effective in-charge state for this site:
   *  - org_role 'runs_jobs' → true
   *  - org_role 'works_for_others' → false
   *  - org_role 'both' → uses site.in_charge
   */
  inCharge: boolean;
}

export function computeInCharge(site: Site | null, org: OrgMembership | null): boolean {
  if (!org) return false;
  if (org.org_role === 'runs_jobs') return true;
  if (org.org_role === 'works_for_others') return false;
  return Boolean(site?.in_charge);
}

// ---------- The actual checklist rules -------------------------------------

/**
 * Build the checklist for a given site + org. Order matters — the page
 * renders in this order and groups by priority within that order.
 */
export function buildSiteChecklist(ctx: Ctx): SetupItem[] {
  const out: SetupItem[] = [];
  const { site, org, inCharge } = ctx;
  const projectType = site.project_type ?? 'full_site';     // default to fullest set if unknown
  const activities = org.risky_activities ?? [];

  // ============================================================
  // EVERY SITE NEEDS THESE
  // ============================================================

  out.push({
    id: 'rams',
    title: 'Pick a RAMS for the work',
    why: 'Every job needs a Risk Assessment & Method Statement for the task being done.',
    priority: 'required',
    target: { kind: 'rams' },
  });

  out.push({
    id: 'sign_in_qr',
    title: 'Generate site sign-in QR code',
    why: 'Anyone on site should sign in. Print the QR and stick it at the gate / cabin / front door.',
    priority: 'required',
    target: { kind: 'sign_in' },
  });

  if (projectType !== 'quick_job') {
    out.push({
      id: 'coshh',
      title: 'COSHH register',
      why: 'Any chemicals on site (cement, paint, fuel, cleaners) — record what they are and the hazards.',
      priority: 'required',
      target: { kind: 'coshh' },
    });
  } else {
    out.push({
      id: 'coshh',
      title: 'COSHH (only if you bring chemicals)',
      why: 'Skip if no hazardous products. Otherwise list them.',
      priority: 'optional',
      target: { kind: 'coshh' },
    });
  }

  // ============================================================
  // PRINCIPAL CONTRACTOR / IN-CHARGE ONLY
  // ============================================================

  if (inCharge) {
    if (projectType === 'full_site') {
      out.push({
        id: 'cpp',
        title: 'Construction Phase Plan (CPP)',
        why: 'Required by CDM 2015 reg. 12 / 15 when more than one contractor is on site. Must exist before work starts.',
        priority: 'required',
        target: { kind: 'document', templateId: 'construction_phase_plan' },
      });

      out.push({
        id: 'f10',
        title: 'F10 — HSE project notification',
        // F10 is filed on HSE's own portal — not in Safe and Sorted.
        // We just link the user out; the checklist tick is manual.
        why: 'Required if the job exceeds 30 working days OR 500 person-days. Submit on HSE\'s portal — link below.',
        priority: 'required',
        // HSE moved the online F10 to form.hse.gov.uk/f10 (was
        // notifications.hse.gov.uk/F10 which now 404s). Point straight at
        // the form landing so users can hit "Create a Notification" without
        // detouring through HSE's guidance page.
        target: { kind: 'external_link', href: 'https://form.hse.gov.uk/f10' },
      });

      out.push({
        id: 'pci',
        title: 'Pre-Construction Information (PCI) on file',
        why: 'Client / Principal Designer must provide. Hazards, services, asbestos, ground conditions.',
        priority: 'required',
        // Template ID has to match an entry in data/docTemplates.ts — the
        // canonical PCI template is 'pre_construction_information'. The
        // previous 'pci_register' ID didn't exist, so clicking the row
        // returned "Template not found."
        target: { kind: 'document', templateId: 'pre_construction_information' },
      });

      out.push({
        id: 'site_setup_record',
        title: 'Site setup record',
        why: 'Welfare, hoarding, services, first aid location, A&E hospital. Reference for your CPP.',
        priority: 'recommended',
        // Repointed from missing 'site_setup' → 'site_welfare_plan' which is
        // the closest existing template (welfare + first aid + hospital info).
        target: { kind: 'document', templateId: 'site_welfare_plan' },
      });

      out.push({
        id: 'first_aiders',
        title: 'Name first aiders for this site',
        why: 'At least one trained first-aider must be on site whenever work is in progress.',
        priority: 'required',
        target: { kind: 'training' },
      });
    } else if (projectType === 'small_refurb') {
      out.push({
        id: 'site_setup_record',
        title: 'Site setup record',
        why: 'Welfare, services, first aid location. Even on small jobs, you need the basics on file.',
        priority: 'recommended',
        // Same repoint as above — 'site_setup' isn't a real template.
        target: { kind: 'document', templateId: 'site_welfare_plan' },
      });
    }

    // Subbie management — applies to any site where you're in charge AND there might be subbies
    if (projectType !== 'quick_job') {
      out.push({
        id: 'subbie_pqq',
        title: 'PQQ each sub-contractor before they start',
        why: 'Pre-Qualification Questionnaire — check insurance, RAMS, competence before they\'re on site.',
        priority: 'recommended',
        target: { kind: 'document', templateId: 'subcontractor_pqq' },
      });

      out.push({
        id: 'subbie_induction',
        title: 'Induct subbies on day one',
        why: 'Each new person on site signs the induction form before starting work.',
        priority: 'recommended',
        target: { kind: 'subbie_induction' },
      });
    }

    if (projectType === 'full_site') {
      out.push({
        id: 'tbt',
        title: 'Toolbox talks programme',
        why: 'Weekly safety briefings on hazards relevant to that week\'s work. Record who attended.',
        priority: 'recommended',
        target: { kind: 'tbt' },
      });
    }
  }

  // ============================================================
  // RISKY-ACTIVITY ADDITIONS (regardless of project type / role)
  // ============================================================

  if (activities.includes('height')) {
    out.push({
      id: 'permit_height',
      title: 'Permit-to-work at height',
      why: 'You ticked "working above 2m" in your profile. Issue a permit before any height work.',
      priority: 'recommended',
      target: { kind: 'permit', permitTemplateId: 'height' },
    });
  }
  if (activities.includes('excavation')) {
    out.push({
      id: 'permit_dig',
      title: 'Permit-to-Dig',
      why: 'No breaking ground without a CAT-scan and a signed permit. Required by your profile.',
      priority: 'required',
      target: { kind: 'permit', permitTemplateId: 'dig' },
    });
  }
  if (activities.includes('confined_spaces')) {
    out.push({
      id: 'permit_confined',
      title: 'Confined-space permit',
      why: 'Manholes, tanks, lift pits. Must have entry permit, attendant, and rescue plan.',
      priority: 'required',
      target: { kind: 'permit', permitTemplateId: 'confined' },
    });
  }
  if (activities.includes('hot_works')) {
    out.push({
      id: 'permit_hot',
      title: 'Hot works permit',
      why: 'Welding, cutting, grinding. Permit + extinguisher + fire watch for 60 mins after.',
      priority: 'required',
      target: { kind: 'permit', permitTemplateId: 'hot_works' },
    });
  }
  if (activities.includes('asbestos')) {
    out.push({
      id: 'asbestos_survey',
      title: 'Asbestos refurb / demolition survey',
      why: 'Pre-2000 buildings. Client should provide. If not held — STOP and request before any disturbance.',
      priority: 'required',
      // No in-app template — asbestos surveys are third-party documents from
      // a licensed surveyor. Link to HSE asbestos guidance instead so users
      // can look up what a valid survey looks like, and tick this row
      // manually once they've got the survey from their client / surveyor.
      target: { kind: 'external_link', href: 'https://www.hse.gov.uk/asbestos/managing/survey.htm' },
    });
  }
  if (activities.includes('lifting')) {
    out.push({
      id: 'lift_plan',
      title: 'Lift plan + LOLER cert',
      why: 'Crane, telehandler, hoist, slings. Lift plan signed by competent person; LOLER cert in date.',
      priority: 'required',
      // Same rationale as asbestos above — lift plans come from a competent
      // lift supervisor / appointed person, not from an in-app template.
      // Link out to HSE LOLER guidance.
      target: { kind: 'external_link', href: 'https://www.hse.gov.uk/work-equipment-machinery/loler.htm' },
    });
  }

  return out;
}

// ---------- Group by priority ----------------------------------------------

export function groupByPriority(items: SetupItem[]): Record<SetupPriority, SetupItem[]> {
  const out: Record<SetupPriority, SetupItem[]> = { required: [], recommended: [], optional: [] };
  for (const it of items) out[it.priority].push(it);
  return out;
}

// ---------- "Not required for this site" — localStorage flag ---------------

const NR_KEY = (siteId: string) => `hs_setup_nr_${siteId}`;

export function getNotRequiredIds(siteId: string): Set<string> {
  try {
    const raw = localStorage.getItem(NR_KEY(siteId));
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch { return new Set(); }
}

export function setNotRequired(siteId: string, itemId: string, value: boolean): void {
  try {
    const ids = getNotRequiredIds(siteId);
    if (value) ids.add(itemId); else ids.delete(itemId);
    localStorage.setItem(NR_KEY(siteId), JSON.stringify([...ids]));
  } catch { /* noop */ }
}

// ---------- Setup mode (sticky "Back to setup" banner) -----------------------
// While the user is walking the checklist, we set a flag in localStorage.
// AppShell reads it and shows a "Back to site setup" sticky banner on every
// page so the user can always return to the checklist.

const SETUP_MODE_KEY = 'hs_setup_active_site';

export function isInSetupMode(): string | null {
  try {
    return localStorage.getItem(SETUP_MODE_KEY) || null;
  } catch { return null; }
}

export function enterSetupMode(siteId: string): void {
  try { localStorage.setItem(SETUP_MODE_KEY, siteId); } catch { /* noop */ }
}

export function exitSetupMode(): void {
  try { localStorage.removeItem(SETUP_MODE_KEY); } catch { /* noop */ }
}
